import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { motion } from "framer-motion";

export default function QuestionRenderer({ question, value, onChange }) {
  const { question_type, question_text, answer_options } = question;
  const parsedOptions = typeof answer_options === "string" ? JSON.parse(answer_options) : answer_options;

  return (
    <motion.div
      data-testid={`question-${question.id}`}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-5"
    >
      <h3 className="font-heading text-xl md:text-2xl font-semibold text-white leading-snug">
        {question_text}
      </h3>

      {question_type === "single_choice" && parsedOptions && (
        <div className="space-y-3">
          {parsedOptions.map((opt, i) => {
            const optValue = typeof opt === "object" ? opt.value : opt;
            const optLabel = typeof opt === "object" ? opt.label : opt;
            const isSelected = value === optValue;
            return (
              <button
                key={i}
                data-testid={`option-${question.id}-${i}`}
                onClick={() => onChange(optValue)}
                className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
                  isSelected
                    ? "bg-[#59C3A6]/15 border-[#59C3A6]/40 text-white"
                    : "bg-[#112A4F]/30 border-white/5 text-slate-300 hover:border-[#59C3A6]/20 hover:bg-[#112A4F]/50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                    isSelected ? "border-[#59C3A6] bg-[#59C3A6]" : "border-slate-600"
                  }`}>
                    {isSelected && <div className="w-2 h-2 rounded-full bg-[#0B1F3B]" />}
                  </div>
                  <span className="text-sm md:text-base">{optLabel}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {question_type === "multi_choice" && parsedOptions && (
        <div className="space-y-3">
          {parsedOptions.map((opt, i) => {
            const optValue = typeof opt === "object" ? opt.value : opt;
            const optLabel = typeof opt === "object" ? opt.label : opt;
            const currentValues = Array.isArray(value) ? value : [];
            const isSelected = currentValues.includes(optValue);
            return (
              <button
                key={i}
                data-testid={`option-${question.id}-${i}`}
                onClick={() => {
                  const newValues = isSelected
                    ? currentValues.filter((v) => v !== optValue)
                    : [...currentValues, optValue];
                  onChange(newValues);
                }}
                className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
                  isSelected
                    ? "bg-[#59C3A6]/15 border-[#59C3A6]/40 text-white"
                    : "bg-[#112A4F]/30 border-white/5 text-slate-300 hover:border-[#59C3A6]/20 hover:bg-[#112A4F]/50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                    isSelected ? "border-[#59C3A6] bg-[#59C3A6]" : "border-slate-600"
                  }`}>
                    {isSelected && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6L5 9L10 3" stroke="#0B1F3B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <span className="text-sm md:text-base">{optLabel}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {question_type === "scale_1_10" && (
        <div>
          <div className="flex justify-between mb-3 text-xs text-slate-500">
            <span>Pas du tout</span>
            <span>Tout à fait</span>
          </div>
          <div className="grid grid-cols-10 gap-2">
            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                data-testid={`scale-${question.id}-${n}`}
                onClick={() => onChange(n)}
                className={`aspect-square rounded-lg flex items-center justify-center text-sm font-semibold transition-all duration-200 ${
                  value === n
                    ? "bg-[#59C3A6] text-[#0B1F3B] scale-110 shadow-[0_0_15px_rgba(89,195,166,0.4)]"
                    : "bg-[#112A4F]/40 text-slate-400 hover:bg-[#112A4F]/70 border border-white/5"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      )}

      {question_type === "free_text" && (
        <Textarea
          data-testid={`textarea-${question.id}`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Votre réponse..."
          rows={4}
          className="bg-[#0F2545] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#59C3A6] focus:ring-1 focus:ring-[#59C3A6] resize-none"
        />
      )}
    </motion.div>
  );
}
